'use client'

import {
  alpha,
  Box,
  ListItemText,
  MenuItem,
  MenuList,
  Typography,
} from '@mui/material'
import { CheckIcon } from 'icons'
import { useState } from 'react'
import { FilterDropdown } from '../FilterDropdown'
import { SearchField } from '../SearchField'

export interface FilterSelectOption {
  value: string
  label: string
  // A second line beneath the label, for an option whose label needs qualifying (e.g. the date
  // range a time period covers).
  subLabel?: string
}

// The row shape shared by the options and the optional cleared-state row.
interface RowContent {
  label: string
  subLabel?: string
}

// A single-select filter dropdown. With `searchable`, the panel opens with a
// search input that narrows the option list. Picking an option closes the panel;
// the active chip's X clears the selection (the panel's redundant "Clear" link
// is omitted).
export function FilterSelect({
  label,
  summaryPrefix,
  formatSummary,
  options,
  value,
  onChange,
  nullOption,
  showSelectedCheck = false,
  searchable = false,
  searchPlaceholder = 'Search…',
  fullWidth = false,
  highlightSelected = true,
}: {
  label: string
  // Chip text when active is `${summaryPrefix}: ${selected label}`. Optional
  // when `formatSummary` is supplied, which then owns the chip text entirely.
  summaryPrefix?: string
  // Derives the active chip text from the selected option, letting the chip
  // show a short form (e.g. "Lesson 1") while the option list keeps the full
  // label (e.g. "Lesson 1: Intro").
  formatSummary?: (option: FilterSelectOption) => string
  options: FilterSelectOption[]
  value: string | null
  onChange: (value: string | null) => void
  // An explicit row for the cleared state, listed first — it's the widest choice
  // and the one a user returns to, so it reads as the top of the list rather
  // than a footnote. For a selector whose default is itself a real choice ("All
  // time"), so it can be highlighted and checked in the list rather than living
  // only as the trigger's placeholder. Picking it clears the value.
  nullOption?: RowContent
  // Marks the selected row with a checkmark as well as the wash.
  showSelectedCheck?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  // Stretches the trigger to fill its container, pushing label and caret/clear
  // to opposite edges — a full-width field rather than a content-sized pill.
  fullWidth?: boolean
  // When false, the trigger chip stays neutral once a value is picked (no
  // primary wash/border) — for selectors where the choice isn't a "filter is
  // active" signal. The selected option row is highlighted regardless.
  highlightSelected?: boolean
}) {
  const [query, setQuery] = useState('')

  const selected = options.find((option) => option.value === value) ?? null
  const summary = selected
    ? (formatSummary?.(selected) ??
      (summaryPrefix ? `${summaryPrefix}: ${selected.label}` : selected.label))
    : null
  const matchesQuery = (text: string) =>
    text.toLowerCase().includes(query.toLowerCase().trim())
  const visibleOptions = query
    ? options.filter((option) => matchesQuery(option.label))
    : options
  const visibleNullOption =
    nullOption && (!query || matchesQuery(nullOption.label)) ? nullOption : null

  return (
    <FilterDropdown
      label={label}
      summary={summary}
      fullWidth={fullWidth}
      highlightSelected={highlightSelected}
      onClear={() => {
        onChange(null)
      }}
      showClearLink={false}
    >
      {(close) => {
        const pick = (next: string | null) => {
          onChange(next)
          setQuery('')
          close()
        }

        return (
          <>
            {searchable ? (
              <Box sx={{ px: 2, py: 1 }}>
                <SearchField
                  value={query}
                  onChange={setQuery}
                  placeholder={searchPlaceholder}
                  fullWidth
                />
              </Box>
            ) : null}

            <MenuList sx={{ maxHeight: 320, overflowY: 'auto' }}>
              {visibleNullOption ? (
                <OptionRow
                  content={visibleNullOption}
                  selected={value === null}
                  showCheck={showSelectedCheck}
                  onSelect={() => {
                    pick(null)
                  }}
                />
              ) : null}
              {visibleOptions.map((option) => (
                <OptionRow
                  key={option.value}
                  content={option}
                  selected={option.value === value}
                  showCheck={showSelectedCheck}
                  onSelect={() => {
                    // Re-picking the current option clears it — except when there's an explicit
                    // cleared row, which makes the panel read as a radio group where re-confirming
                    // your own choice landing you somewhere else would be a surprise.
                    pick(
                      option.value === value && !nullOption
                        ? null
                        : option.value
                    )
                  }}
                />
              ))}
              {visibleOptions.length === 0 && !visibleNullOption ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ px: 2, py: 1 }}
                >
                  No matches
                </Typography>
              ) : null}
            </MenuList>
          </>
        )
      }}
    </FilterDropdown>
  )
}

function OptionRow({
  content,
  selected,
  showCheck,
  onSelect,
}: {
  content: RowContent
  selected: boolean
  showCheck: boolean
  onSelect: () => void
}) {
  return (
    <MenuItem
      dense
      sx={{
        py: 0.75,
        px: 2,
        gap: 1,
        minHeight: 0,
        // The chosen row gets the same light-teal wash as an active
        // FilterDropdown chip, instead of MUI's default grey select.
        '&.Mui-selected': {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
          },
        },
      }}
      selected={selected}
      onClick={onSelect}
    >
      <ListItemText
        primary={content.label}
        secondary={content.subLabel}
        slotProps={{
          primary: { variant: 'body2' },
          secondary: { variant: 'caption' },
        }}
      />
      {showCheck && selected ? (
        // 18px, matching the row's two-line height rather than the body text.
        <CheckIcon sx={{ fontSize: '1.125rem', color: 'primary.main' }} />
      ) : null}
    </MenuItem>
  )
}
